import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import * as URX_FORM from '../constants/urx-form';
import { COMBINED_USERS, UT30MaximoSaaS } from '../constants/spread';
import { Helmet } from 'react-helmet';
import {
  Button,
  ButtonSet,
  ContentSwitcher,
  InlineNotification,
  NumberInput,
  RadioTile,
  SkeletonPlaceholder,
  Switch,
  Tag,
  TileGroup,
  Toggle,
  Tooltip,
  Modal,
} from 'carbon-components-react';
import './MASSaaSPriceEstimator.scss';
import 'src/common/style/common.scss';
import { usePrice } from '../hooks/usePrice';
import Cookies from 'js-cookie';
import Footnote from './Footnote';
import messages from '../locales/messages';
import Thanks from './Thanks';
import { segmentsTracking } from 'src/common/lib/tracker';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import {
  getMessage,
  useLang,
  determineCountry,
  LangDefaults,
} from 'src/common/hooks/widgets/useLang';
import {
  Package,
  Tiles,
  authorizedUsers,
  concurrentUsers,
  essentialsEnv,
  essentialsUsers,
  estInspectionTableData,
  estMaintenanceTableData,
  healthScores,
  standardAppPoints,
  switchBrand,
} from '../constants/default';
import { useNumberFormat } from 'src/common/hooks/widgets/useNumberFormat';
import { FormModal } from 'src/common/utils/FormModal';
import { Launch16 } from '@carbon/icons-react';
import buyNowCountryData from '../data/buy.json';
import { shortcode } from '../MASSaaSPriceEstimator.widget';
import countryList from '../services/currencies.json';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';
import { createUseTranslation } from '../../../common/hooks/widgets/useTranslation';
import { useMASSAASSelector } from '../lib/redux/hooks';

export interface MASSaaSPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const MASSaaSPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: MASSaaSPriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [urxlang] = useState(localeCode.toString());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const [tileValueSelected, setTileValueSelected] = useState(Tiles.tile1);
  const [packageName, setPackageName] = useState(Package.pkg1);
  const [userAmount, setUserAmount] = useState(essentialsUsers.default);
  const [envAmount, setEnvAmount] = useState(essentialsEnv.default);
  const [showBuy, setShowBuy] = useState(false);
  const [buyNowUrl, setBuyNowUrl] = useState('');
  const [concurrentUsersAmt, setConcurrentUsersAmt] = useState(
    concurrentUsers.default,
  );
  const [authUsersAmt, setAuthUsersAmt] = useState(authorizedUsers.default);
  const [healthScoresAmt, setHealthScoresAmt] = useState(healthScores.default);
  const [additionalUsers, setAdditionalUsers] = useState(false);
  const [nonProdEnv, setNonProdEnv] = useState(false);
  const concurrentUserRef = useRef() as MutableRefObject<HTMLInputElement>;
  const authUserRef = useRef() as MutableRefObject<HTMLInputElement>;
  const healthScoresRef = useRef() as MutableRefObject<HTMLInputElement>;
  const cookieURL = useCookiesURL();
  const [open, setOpen] = useState(false);
  const [onInspection, setOnInspection] = useState(false);
  const [totalStdBaseAppPoints, setTotalStdBaseAppPoints] = useState(
    standardAppPoints.default,
  );
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');

  // lang and userlocale is based out of ddo
  const [lang, userLocale, ddo] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  const [totalCost, totalCostMonthly, loading] = usePrice({
    userAmount,
    envAmount,
    tileValueSelected,
    additionalUsers,
    nonProdEnv,
    onInspection,
    currency,
    localeCode,
    concurrentUsersAmt,
    authUsersAmt,
    healthScoresAmt,
    setTotalStdBaseAppPoints,
    country,
  });
  const [formattedAuthUsers] = useNumberFormat(authUsersAmt);
  const [formattedHealthScores] = useNumberFormat(healthScoresAmt);
  const [formattedStdBaseAppPoints] = useNumberFormat(totalStdBaseAppPoints);

  const { bpName, logo, companyId, desc } = useBPName(UT30MaximoSaaS, true);

  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);

  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound === null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const AEMMessages = useMASSAASSelector(
    (state) => state.translations.messages,
  );
  const useTranslation = createUseTranslation(messages, AEMMessages);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const invalidConcurrentUsers =
    parseInt(concurrentUserRef?.current?.value) < concurrentUsers.min ||
    parseInt(concurrentUserRef?.current?.value) > concurrentUsers.max ||
    concurrentUserRef?.current?.value === '';

  const invalidAuthUsers =
    parseInt(authUserRef?.current?.value) < authorizedUsers.min ||
    parseInt(authUserRef?.current?.value) > authorizedUsers.max ||
    authUserRef?.current?.value === '';

  const invalidHealthScores =
    parseInt(healthScoresRef?.current?.value) < healthScores.min ||
    parseInt(healthScoresRef?.current?.value) > healthScores.max ||
    healthScoresRef?.current?.value === '';

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  useEffect(() => {
    // On pre-prod and prod widget will have the the parent div with shortcode classname.
    // So we are applying margin because some AEM classname causing style issue.
    const shortCodeStyle = Array.from(
      document.getElementsByClassName(
        shortcode,
      ) as HTMLCollectionOf<HTMLElement>,
    )[0];
    // In local shortcode will be alway undefined, to avoid app crash on local we are
    // checking the below condition and it will only work in pre-prod and prod version.
    if (shortCodeStyle !== undefined) {
      shortCodeStyle.style.marginLeft = '1rem';
    }
  }, []);

  useEffect(() => {
    setCookie();
  }, [
    userAmount,
    totalCostMonthly,
    totalCost,
    packageName,
    tileValueSelected,
    onInspection,
  ]);

  useEffect(() => {
    if (tileValueSelected === Tiles.tile2) {
      setConcurrentUsersAmt(concurrentUsers.default);
      setAuthUsersAmt(authorizedUsers.default);
      setHealthScoresAmt(healthScores.default);
    } else if (tileValueSelected === Tiles.tile1) {
      setUserAmount(essentialsUsers.default);
      setEnvAmount(essentialsEnv.default);
      setAdditionalUsers(false);
      setNonProdEnv(false);
    }
  }, [tileValueSelected]);

  useEffect(() => {
    if (ddo) {
      buyNowCountryData.map((item) => {
        if (item.ddoLoc === ddo) {
          if (!onInspection) {
            setBuyNowUrl(item.maintenanceUrl);
            setShowBuy(true);
          } else {
            setBuyNowUrl(item.inspectionUrl);
            setShowBuy(true);
          }
        }
      });
    }
  }, [ddo, tileValueSelected, onInspection]);

  const setCookie = () => {
    const essentialsPkgInfo = !onInspection
      ? 'Maintenance Essentials'
      : 'Inspection Essentials';
    const essentialsInfo = !onInspection
      ? ' Users: ' +
        userAmount +
        ' | Environment: ' +
        envAmount +
        ' | Work Orders / Hour: ' +
        '100' +
        ' | Health Scores / Hour: ' +
        '1,000'
      : 'Client Devices: ' +
        '5' +
        ' | Inferences / Hour: ' +
        '10,000' +
        ' | Storage: ' +
        '500 GB';
    healthScoresAmt;
    const packageInfo =
      tileValueSelected === Tiles.tile1
        ? essentialsInfo + ' | Package: ' + essentialsPkgInfo
        : ' Concurrent Users: ' +
          concurrentUsersAmt +
          ' | Authorized Users: ' +
          formattedAuthUsers +
          ' | Health Scores: ' +
          formattedHealthScores +
          ' | AppPoints: ' +
          formattedStdBaseAppPoints +
          ' | Package: ' +
          packageName;

    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            packageInfo +
            ' | Estimated Monthly cost: ' +
            totalCostMonthly +
            ' | Estimated Annual cost: ' +
            totalCost,
        },
      }),
    );
  };

  const handleTakeMe = () => {
    if (tileValueSelected === Tiles.tile1) {
      setTileValueSelected(Tiles.tile2);
    } else if (tileValueSelected === Tiles.tile2) {
      setTileValueSelected(Tiles.tile3);
    } else {
      setTileValueSelected(Tiles.tile1);
    }
  };
  const mntEssentialsFeature = [
    useTranslation('est_mnt_feat_pt1') as string,
    useTranslation('est_mnt_feat_pt2') as string,
    useTranslation('est_mnt_feat_pt3') as string,
    useTranslation('est_mnt_feat_pt4') as string,
    useTranslation('est_mnt_feat_pt5') as string,
  ];

  const inspEssentialsFeature = [
    useTranslation('est_insp_feat_pt1') as string,
    useTranslation('est_insp_feat_pt2') as string,
  ];

  const standardFeature = [
    useTranslation('std_feat_pt1') as string,
    useTranslation('std_feat_pt2') as string,
    useTranslation('std_feat_pt3') as string,
    useTranslation('std_feat_pt4') as string,
  ];

  const premiumFeature = [
    useTranslation('prem_feat_pt1') as string,
    useTranslation('prem_feat_pt2') as string,
    useTranslation('prem_feat_pt3') as string,
    useTranslation('prem_feat_pt4') as string,
  ];

  const recommendText =
    tileValueSelected === Tiles.tile1
      ? useTranslation('recommend_text_std')
      : useTranslation('recommend_text_prem');
  const premNotificationLabel = useTranslation('prem_notif_label');

  const combinedTotalUsers = Math.round(authUsersAmt / 3) + concurrentUsersAmt;
  const yes = useTranslation('yes') as string;
  const no = useTranslation('no') as string;

  const appIncludeInfo = [
    useTranslation('manage') as string,
    useTranslation('health') as string,
  ];

  type ModalStateManagerProps = {
    renderLauncher: any;
    children: React.ComponentType<LauncherContentProps>;
  };

  type LauncherContentProps = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };

  const ModalStateManager: React.FC<ModalStateManagerProps> = ({
    renderLauncher: LauncherContent,
    children: ModalContent,
  }) => {
    return (
      <>
        {!ModalContent || typeof document === 'undefined'
          ? null
          : ReactDOM.createPortal(
              <ModalContent open={open} setOpen={setOpen} />,
              document.body,
            )}
        {LauncherContent && <LauncherContent open={open} setOpen={setOpen} />}
      </>
    );
  };

  const handleModal1 = () => {
    setOpen(true);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'Maximo SaaS Pricing Page',
      object: 'Pricing page',
      resultValue: 'CTA clicked',
      CTA: 'Modal open ' + modalLocalCountry,
      location: 'Estimator',
      action: '',
      field: '',
    });
  };

  useEffect(() => {
    if (localeCode.includes('ar')) {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
  }, [localeCode]);

  return (
    <div className="mas_saas">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          usersAmount={userAmount}
          envCount={envAmount}
          healthScoresAmount={formattedHealthScores}
          concurrentAmount={concurrentUsersAmt}
          authorizedAmount={formattedAuthUsers}
          appPoints={formattedStdBaseAppPoints}
          onInspection={onInspection}
          tileValue={tileValueSelected}
          licensePriceMonthly={`${totalCostMonthly}`}
          packageName={packageName}
          bpName={bpName}
        />
      ) : (
        <div id="MASSaaSGrid" className="bx--grid widget-styles">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mar3">
            <div
              className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 bx--offset-lg-12"
              id="countryModal"
            >
              {localExist && toggleCountry ? (
                <ModalStateManager
                  renderLauncher={() => (
                    <Button onClick={handleModal1}>
                      Access Page in
                      {' ' + modalLocalLanguage}
                    </Button>
                  )}
                >
                  {({ open, setOpen }) => (
                    <Modal
                      open={open}
                      onRequestClose={() => setOpen(false)}
                      modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency})`}
                      modalHeading={`The Maximo PE SaaS Price estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                      size={'sm'}
                      primaryButtonText={'Cool! Take me there'}
                      secondaryButtonText={'Stay here'}
                      onRequestSubmit={() => {
                        localStorage.setItem('langLocale', selectedLocale);
                        (window.location.href =
                          'https://www.ibm.com/' +
                          selectedLocale +
                          '/products/maximo/pricing'),
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'Maximo SaaS Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Switched to country ' + modalLocalCountry,
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        return true;
                      }}
                      onSecondarySubmit={() => {
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Maximo SaaS Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Stay here button clicked ' + modalLocalCountry,
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                        setOpen(false);
                        return true;
                      }}
                    ></Modal>
                  )}
                </ModalStateManager>
              ) : null}
            </div>
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="page_sub_heading ">
                {useTranslation('maas_saas_headline')}
              </div>
              <p className="page_body padding_24_3 mar2 ">
                {useTranslation('maas_saas_sub_headline')}
              </p>
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                  <TileGroup
                    className="mas_saas__tilegroup"
                    name="radio tile group"
                    valueSelected={tileValueSelected}
                  >
                    <RadioTile
                      className="mas_saas__tile"
                      id="radio-tile-1-mas_saas"
                      value="essentials"
                      onClick={() => {
                        setTileValueSelected(Tiles.tile1);
                        setPackageName(Package.pkg1);
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Maximo SaaS Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Essentials Tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <div className="page_sub_heading mar8">
                        {Package.pkg1}
                      </div>
                      <div className="page_body_2">
                        {useTranslation('est_pkg_desc')}
                      </div>
                    </RadioTile>
                    <RadioTile
                      id="radio-tile-2-mas_saas"
                      value="standard"
                      className="mas_saas__tile"
                      onClick={() => {
                        setTileValueSelected(Tiles.tile2);
                        setPackageName(Package.pkg2);
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Maximo SaaS Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Standard Tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <div className="page_sub_heading mar8">
                        {Package.pkg2}
                      </div>
                      <div className="page_body_2">
                        {useTranslation('std_pkg_desc')}
                      </div>
                    </RadioTile>
                    <RadioTile
                      id="radio-tile-3-mas_saas"
                      value="premium"
                      onClick={() => {
                        setTileValueSelected(Tiles.tile3);
                        setPackageName(Package.pkg3);
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Maximo SaaS Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Premium Tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <div className="page_sub_heading mar8">
                        {Package.pkg3}
                      </div>
                      <div className="page_body_2">
                        {useTranslation('prem_pkg_desc')}
                      </div>
                    </RadioTile>
                  </TileGroup>
                </div>
              </div>
              <div className="bx--row mar9">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                  {tileValueSelected === Tiles.tile1 && (
                    <div className="bx--row mar9">
                      <div className="bx--col-lg-12 bx--col-md-16 bx--col-sm-16">
                        <ContentSwitcher
                          className="content-switcher"
                          selectedIndex={onInspection ? 1 : 0}
                          size="lg"
                          onChange={(obj) => {
                            const { name } = obj;
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'Maximo SaaS Pricing Page',
                              object: 'Pricing page',
                              resultValue: `CTA clicked ${name}`,
                              CTA: 'Essential switch',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            setOnInspection(
                              name === switchBrand.brand1 ? false : true,
                            );
                          }}
                        >
                          <Switch
                            text={switchBrand.brand1}
                            name={switchBrand.brand1}
                          />
                          <Switch
                            text={switchBrand.brand2}
                            name={switchBrand.brand2}
                          />
                        </ContentSwitcher>
                      </div>
                    </div>
                  )}

                  <div className="mar3">
                    {tileValueSelected === Tiles.tile1 ? (
                      <>
                        <div className="page_body_2 ">
                          {useTranslation('est_feat_label')}
                        </div>
                        {!onInspection ? (
                          <div>
                            {mntEssentialsFeature.map(
                              (item: string, index: number) => (
                                <Tag
                                  key={index}
                                  type={'purple'}
                                  size="md"
                                  disabled={false}
                                  filter={false}
                                  className="tag-margin"
                                >
                                  {item}
                                </Tag>
                              ),
                            )}
                          </div>
                        ) : (
                          <div>
                            {inspEssentialsFeature.map(
                              (item: string, index: number) => (
                                <Tag
                                  key={index}
                                  type={'purple'}
                                  size="md"
                                  disabled={false}
                                  filter={false}
                                  className="tag-margin"
                                >
                                  {item}
                                </Tag>
                              ),
                            )}
                          </div>
                        )}
                      </>
                    ) : tileValueSelected === Tiles.tile2 ? (
                      <>
                        <div className="page_body_2 ">
                          {useTranslation('std_feat_label')}:
                        </div>
                        <div>
                          {standardFeature.map(
                            (item: string, index: number) => (
                              <Tag
                                key={index}
                                type={'purple'}
                                size="md"
                                disabled={false}
                                filter={false}
                                className="tag-margin"
                              >
                                {item}
                              </Tag>
                            ),
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="page_body_2 ">
                          {useTranslation('prem_feat_label')}:
                        </div>
                        <div>
                          {premiumFeature.map((item: string, index) => (
                            <Tag
                              key={index}
                              type={'purple'}
                              size="md"
                              disabled={false}
                              filter={false}
                              className="tag-margin"
                            >
                              {item}
                            </Tag>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div
                    className={`bx--row mar3 ${
                      tileValueSelected === Tiles.tile3 ? 'mar5' : ''
                    }`}
                  >
                    {tileValueSelected === Tiles.tile1 ? (
                      <>
                        {!onInspection && (
                          <>
                            <div className="bx--col-lg-7 bx--col-md-16 bx--col-sm-16 mar1">
                              <div className="page_body_2 ">
                                {useTranslation('toggle1_text')}
                              </div>
                              <div>
                                <Toggle
                                  size="md"
                                  labelText={
                                    useTranslation('toggle1_info_pt1') as string
                                  }
                                  labelA={no}
                                  labelB={yes}
                                  id="additional-users"
                                  toggled={additionalUsers}
                                  onChange={() => {
                                    segmentsTracking('CTA Clicked', {
                                      pageTitle: 'Maximo SaaS Pricing Page',
                                      object: 'Pricing page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Additional User toggle',
                                      location: 'Estimator',
                                      action: '',
                                      field: '',
                                    });
                                    setAdditionalUsers(!additionalUsers);
                                    if (!additionalUsers) {
                                      setUserAmount(essentialsUsers.max);
                                    } else {
                                      setUserAmount(essentialsUsers.default);
                                    }
                                  }}
                                />
                              </div>
                              <p className="helper-text ">
                                {useTranslation('toggle1_info_pt2')}
                              </p>
                            </div>
                            <div className="bx--col-lg-1" />
                            <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-16">
                              <div className="page_body_2">
                                {useTranslation('toggle2_text')}
                              </div>
                              <Toggle
                                size="md"
                                labelText={
                                  useTranslation('toggle2_info_pt1') as string
                                }
                                labelA={no}
                                labelB={yes}
                                id="non-prod env"
                                toggled={nonProdEnv}
                                onChange={() => {
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: 'Maximo SaaS Pricing Page',
                                    object: 'Pricing page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Non-Prod Env toggle',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  setNonProdEnv(!nonProdEnv);
                                  if (!nonProdEnv) {
                                    setEnvAmount(essentialsEnv.max);
                                  } else {
                                    setEnvAmount(essentialsEnv.default);
                                  }
                                }}
                              />
                              <p className="helper-text">
                                {useTranslation('toggle2_info_pt2')}
                              </p>
                            </div>
                          </>
                        )}
                        {(additionalUsers || nonProdEnv) && !onInspection && (
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 mar8 ">
                            <InlineNotification
                              title={recommendText}
                              kind="info-square"
                              role="alert"
                              actions={
                                <p
                                  className="notification-text"
                                  onClick={handleTakeMe}
                                >
                                  {useTranslation('std_notif_txt')}
                                </p>
                              }
                            />
                          </div>
                        )}
                        {onInspection && (
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 mar8 ">
                            <InlineNotification
                              title={recommendText}
                              kind="info-square"
                              role="alert"
                              actions={
                                <p
                                  className="notification-text"
                                  onClick={() => {
                                    segmentsTracking('CTA Clicked', {
                                      pageTitle: 'Maximo SaaS Pricing Page',
                                      object: 'Pricing page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Book a Consultation',
                                      location: 'Estimator',
                                      action: '',
                                      field: '',
                                    });
                                    window.open(
                                      ' https://www.ibm.com/products/maximo/pricing/?schedulerform=',
                                      '_self',
                                    );
                                  }}
                                >
                                  {useTranslation('book_text')}
                                </p>
                              }
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="bx--col-lg-8 bx--col-md-3 bx--col-sm-16 ">
                          <Tooltip
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Concurrent Users tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body mb-3">
                                {useTranslation('slider1_text')}
                              </span>
                            }
                          >
                            <span className="slide_text">
                              {useTranslation('tooltip_concurrent_users_text')}
                            </span>
                          </Tooltip>
                          <NumberInput
                            id="concurrent-user"
                            size="md"
                            allowEmpty={true}
                            ref={concurrentUserRef}
                            invalidText={
                              'Please provide range between 0 and 500'
                            }
                            min={concurrentUsers.min}
                            max={concurrentUsers.max}
                            className="number-input mb-5"
                            disabled={tileValueSelected === Tiles.tile3}
                            value={concurrentUsersAmt}
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Concurrent User Input',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setConcurrentUsersAmt(
                                Number(concurrentUserRef.current.value),
                              );
                            }}
                          />
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-3 bx--col-sm-16">
                          <Tooltip
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Authorized Users tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body mb-3">
                                {useTranslation('slider2_text')}
                              </span>
                            }
                          >
                            <span className="slide_text">
                              {useTranslation('tooltip_auth_user_text')}
                            </span>
                          </Tooltip>
                          <NumberInput
                            id="authorized-user"
                            size="md"
                            className="mb-5"
                            allowEmpty={true}
                            min={authorizedUsers.min}
                            max={authorizedUsers.max}
                            ref={authUserRef}
                            disabled={tileValueSelected === Tiles.tile3}
                            invalidText={
                              'Please provide range between 0 and 1,500'
                            }
                            value={authUsersAmt}
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Authorized Users Input',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setAuthUsersAmt(
                                Number(authUserRef.current.value),
                              );
                            }}
                          />
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-3 bx--col-sm-16">
                          <Tooltip
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Health Scores tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body mb-3">
                                {useTranslation('slider3_text')}
                              </span>
                            }
                          >
                            <span className="slide_text">
                              {useTranslation('tooltip_health_text')}
                            </span>
                          </Tooltip>
                          <NumberInput
                            id="health-scores"
                            size="md"
                            allowEmpty={true}
                            min={healthScores.min}
                            max={healthScores.max}
                            ref={healthScoresRef}
                            step={healthScores.default}
                            disabled={tileValueSelected === Tiles.tile3}
                            invalidText={
                              'Please provide range between 1000 and 1,000,000'
                            }
                            value={healthScoresAmt}
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Health Scores Input',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setHealthScoresAmt(
                                Number(healthScoresRef.current.value),
                              );
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {tileValueSelected === Tiles.tile2 && (
                    <>
                      <div className="bx--row mar3">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 ">
                          <div className="page_body_2">
                            {useTranslation('applications')}
                          </div>

                          {appIncludeInfo.map((item: string, index) => (
                            <Tag
                              key={index}
                              type={'teal'}
                              size="md"
                              disabled={false}
                              filter={false}
                              className="tag-margin"
                            >
                              {item}
                            </Tag>
                          ))}
                        </div>
                      </div>
                      <div className="bx--row mar3 mar1">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <p className="page_body ">
                            {useTranslation('widgetText_3')}
                          </p>
                        </div>
                      </div>
                      {(concurrentUsersAmt === concurrentUsers.max ||
                        authUsersAmt === authorizedUsers.max ||
                        healthScoresAmt === healthScores.max ||
                        combinedTotalUsers >= 500) &&
                        !invalidConcurrentUsers &&
                        !invalidHealthScores &&
                        !invalidAuthUsers && (
                          <InlineNotification
                            title={recommendText}
                            kind={'info-square'}
                            actions={
                              <p
                                className="notification-text"
                                onClick={handleTakeMe}
                              >
                                {useTranslation('prem_notif_txt')}
                              </p>
                            }
                          />
                        )}
                    </>
                  )}
                  {tileValueSelected !== Tiles.tile3 && (
                    <div id="footnote" className="div-only-lg">
                      <div className="bx--row">
                        <div className="bx--col-lg-16 ">
                          <div id="footnote" className="div-only-l">
                            <div className="bx--row">
                              <div className="bx--col-lg-16 marginleft_1">
                                <div>
                                  <p>
                                    <span className="footnote_num">1</span>
                                  </p>
                                </div>
                                <div className="footnote_text">
                                  <DisclaimerText
                                    localeCode={localeCode}
                                    product={'Maximo Application Suite'}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="bx--col-lg-1"></div>
                <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-16">
                  {tileValueSelected === Tiles.tile3 ? (
                    <div>
                      <InlineNotification
                        kind="info"
                        role="on-book-consultation"
                        title={premNotificationLabel}
                        subtitle={useTranslation('contact_info')}
                        statusIconDescription="on-book-consultation"
                        hideCloseButton={true}
                      />

                      <ButtonSet className=" btn-set mar4 cta-btn-group">
                        <Button
                          id="request_btn"
                          target="_self"
                          data-ibm-contact="scheduler-link"
                          href="#"
                          onClick={() => {
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'Maximo SaaS Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Book a Consultation',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            window.open(
                              ' https://www.ibm.com/products/maximo/pricing/?schedulerform=',
                              '_self',
                            );
                          }}
                        >
                          {useTranslation('book_text')}
                        </Button>
                      </ButtonSet>
                    </div>
                  ) : (
                    <div id="stickyNav" className="bx--row box-shadow">
                      {tileValueSelected === Tiles.tile1 ? (
                        <Tag type="outline" className="mar4 tag-marginleft">
                          {Package.pkg1}
                        </Tag>
                      ) : tileValueSelected === Tiles.tile2 ? (
                        <Tag type="outline" className="mar4 tag-marginleft">
                          {Package.pkg2}
                        </Tag>
                      ) : (
                        <Tag type="outline" className="mar4 tag-marginleft">
                          {Package.pkg3}
                        </Tag>
                      )}
                      <div className="sticky-nav-table price_table_comp bg_white">
                        <table className="cds--data-table cds--data-table--sm">
                          <thead>
                            <tr>
                              <th scope="col">
                                <div className="price_table_comp__heading">
                                  {useTranslation('col1_text')}
                                </div>
                              </th>
                              <th scope="col">
                                <div className="cds--table-header-label">
                                  <div className="price_table_comp__heading">
                                    {useTranslation('col2_text')}
                                  </div>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          {tileValueSelected === Tiles.tile1 &&
                          !onInspection ? (
                            <tbody aria-live="polite">
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('user_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {useTranslation('est_user_qty_text')}
                                    {`${userAmount}`}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('env_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {envAmount}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('work_order_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {estMaintenanceTableData.workOrdersHour}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('health_score_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {estMaintenanceTableData.healthScoresrHour}
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          ) : tileValueSelected === Tiles.tile1 &&
                            onInspection ? (
                            <tbody aria-live="polite">
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('client_device_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {estInspectionTableData.clientDevice}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('inference_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {estInspectionTableData.inferencesHour}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('storage_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {estInspectionTableData.storage}
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          ) : (
                            <tbody aria-live="polite">
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('concurrent_user')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {concurrentUsersAmt}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('authorized_user')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {formattedAuthUsers}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('health_score_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {formattedHealthScores}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('appPoints_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {totalStdBaseAppPoints <=
                                    standardAppPoints.minPoint
                                      ? standardAppPoints.minPoint
                                      : formattedStdBaseAppPoints}
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          )}
                        </table>
                        <div id="price_toggle_one">
                          <div>
                            {!(
                              concurrentUsersAmt === concurrentUsers.max ||
                              authUsersAmt === authorizedUsers.max ||
                              healthScoresAmt === healthScores.max ||
                              combinedTotalUsers >= COMBINED_USERS
                            ) ? (
                              <>
                                <p
                                  id="widget.estimated-cost"
                                  className="mas_saas__ibm-type-e estmt-cost price_table_comp__bodycopy padding_24_2"
                                >
                                  {useTranslation('estimated_text')}
                                  <sup className="mas_saas__sup">1</sup>
                                </p>

                                <div className="padding_8 price_table_comp__price_lg_bold">
                                  {loading ? (
                                    <SkeletonPlaceholder className="mas_saas__sticky-nav-skeleton" />
                                  ) : (
                                    `${totalCostMonthly}*`
                                  )}
                                </div>
                                <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                                  * {useTranslation('prcie_text')}
                                </p>
                                <ButtonSet
                                  className="cta-btn-group"
                                  style={{ display: 'flex' }}
                                >
                                  <Button
                                    id="request_btn"
                                    onClick={() => {
                                      segmentsTracking('CTA Clicked', {
                                        pageTitle: 'Maximo SaaS Pricing Page',
                                        object: 'Pricing page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Request a quote now',
                                        location: 'Estimator',
                                        action: '',
                                        field: '',
                                      });
                                      initUrxForm(
                                        environment,
                                        URX_FORM.INSTANCE_ID,
                                        URX_FORM.FORM_ID,
                                        urxlang as IBMLocale,
                                        companyId,
                                      );
                                      setDrawerOpen(true);
                                      setCookie();
                                    }}
                                  >
                                    {useTranslation('quote_text')}
                                  </Button>
                                  {tileValueSelected === Tiles.tile1 &&
                                    showBuy && (
                                      <Button
                                        renderIcon={Launch16}
                                        id="buynow_btn"
                                        kind="tertiary"
                                        onClick={() => {
                                          segmentsTracking('CTA Clicked', {
                                            pageTitle:
                                              'Maximo SaaS Pricing Page',
                                            object: 'Pricing page',
                                            resultValue: 'CTA clicked',
                                            CTA: 'Book Now',
                                            location: 'Estimator',
                                            action: '',
                                            field: '',
                                          });
                                          window.open(buyNowUrl);
                                        }}
                                      >
                                        {useTranslation('buy_now')}
                                      </Button>
                                    )}
                                </ButtonSet>
                              </>
                            ) : !invalidConcurrentUsers &&
                              !invalidHealthScores &&
                              !invalidAuthUsers ? (
                              <>
                                <InlineNotification
                                  kind="info"
                                  role="onprem"
                                  title={premNotificationLabel}
                                  subtitle={useTranslation('contact_info')}
                                  statusIconDescription="On-Prem Info"
                                  hideCloseButton={true}
                                />

                                <ButtonSet className="btn-set mar4 cta-btn-group">
                                  <Button
                                    id="request_btn"
                                    target="_self"
                                    data-ibm-contact="scheduler-link"
                                    href="#"
                                    onClick={() => {
                                      segmentsTracking('CTA Clicked', {
                                        pageTitle: 'Maximo SaaS Pricing Page',
                                        object: 'Pricing page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Book a Consultation',
                                        location: 'Estimator',
                                        action: '',
                                        field: '',
                                      });
                                      window.open(
                                        ' https://www.ibm.com/products/maximo/pricing/?schedulerform=',
                                        '_self',
                                      );
                                    }}
                                  >
                                    {useTranslation('book_text')}
                                  </Button>
                                </ButtonSet>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {tileValueSelected !== Tiles.tile3 && (
                    <div id="footnote" className="div-only-media">
                      <div className="bx--row mar9">
                        <div className="bx--col-lg-9 ">
                          <div className="footnote_wrap">
                            <Footnote numNote={1} />
                            <div className="footnote_text">
                              <DisclaimerText
                                localeCode={localeCode}
                                product={'Maximo Application Suite'}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <FormModal
                    pageTitle="Maximo SaaS Pricing Page"
                    object="Maximo SaaS Pricing Page"
                    productTitle={'Maximo Application Suite as a Service'}
                    pageUrl={''}
                    location={'Estimator'}
                    bpName={bpName}
                    logo={logo}
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    formId={URX_FORM.FORM_ID}
                    lang={lang}
                  ></FormModal>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MASSaaSPriceEstimator;
